import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { FaLayerGroup } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";
import MentorAttendanceMatrix from "../attendance/MentorAttendanceMatrix";
import BatchStudents from "./BatchStudents";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Batch {
  id: number;
  name: string;
  topic?: string;
  startingDate?: string;
  endingDate?: string;
  zoomLink?: string;
  students?: any[];
}

const MentorBatches: React.FC = () => {
  const { student } = useAuth();
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [expandedBatchId, setExpandedBatchId] = useState<number | null>(null);
  const [batchStudents, setBatchStudents] = useState<Record<number, any[]>>({});
  const [startingClass, setStartingClass] = useState<Record<number, boolean>>(
    {}
  ); // ✅ NEW

  useEffect(() => {
    if (!student) return;
    setLoading(true);
    fetch(`${API_BASE}/api/mentor/my-batches`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setBatches(Array.isArray(data) ? data : []))
      .catch(() => setError("Failed to fetch batches"))
      .finally(() => setLoading(false));
  }, [student]);

  const toggleStudentList = async (batchId: number) => {
    if (expandedBatchId === batchId) {
      setExpandedBatchId(null);
      return;
    }
    if (!batchStudents[batchId]) {
      try {
        const res = await fetch(
          `${API_BASE}/api/mentor/batch/${batchId}/students`,
          {
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Failed to fetch student list");
        const data = await res.json();
        setBatchStudents((prev) => ({ ...prev, [batchId]: data }));
      } catch (err) {
        console.error(err);
      }
    }
    setExpandedBatchId(batchId);
  };

  const handleStartClass = async (batch: Batch) => {
    if (!batch.zoomLink || !student?.studentId) {
      alert("Missing Zoom link or mentor information.");
      return;
    }

    // ✅ Prevent multiple clicks
    if (startingClass[batch.id]) return;

    try {
      // ✅ Disable button for this specific batch
      setStartingClass((prev) => ({ ...prev, [batch.id]: true }));

      await fetch(
        `${API_BASE}/api/attendance/join?batchId=${batch.id}&userId=${student.studentId}&role=MENTOR`,
        {
          method: "POST",
          credentials: "include",
        }
      ).catch(() => alert("Failed to record mentor attendance"));

      window.open(batch.zoomLink, "_blank", "noopener,noreferrer");
    } finally {
      // ✅ Re-enable button after 2 seconds
      setTimeout(() => {
        setStartingClass((prev) => ({ ...prev, [batch.id]: false }));
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaLayerGroup />}
        title="My Batches"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/mentor/dashboard" },
          { label: "My Batches" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h2 className="text-xl font-bold text-[#009fe3] mb-6">
            Assigned Batches
          </h2>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : batches.length === 0 ? (
            <p className="text-gray-500">
              You don't have any assigned batches.
            </p>
          ) : (
            <div className="space-y-6">
              {batches.map((batch) => (
                <div
                  key={batch.id}
                  className="rounded-xl border border-gray-200 bg-white shadow hover:shadow-md transition p-6"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-semibold text-[#009fe3] mb-1">
                        {batch.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Topic:</span>{" "}
                        {batch.topic || "—"}
                      </p>
                      <p className="text-sm text-gray-700 mb-1">
                        <span className="font-medium">Start Date:</span>{" "}
                        {batch.startingDate
                          ? new Date(batch.startingDate).toLocaleDateString()
                          : "—"}
                      </p>
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">End Date:</span>{" "}
                        {batch.endingDate
                          ? new Date(batch.endingDate).toLocaleDateString()
                          : "—"}
                      </p>
                    </div>
                    <div className="flex flex-col space-y-2 min-w-[160px]">
                      {/* ✅ UPDATED: Start Class Button */}
                      <button
                        onClick={() => handleStartClass(batch)}
                        disabled={startingClass[batch.id]}
                        className={`px-4 py-2 rounded text-sm text-center transition-all ${
                          startingClass[batch.id]
                            ? "bg-gray-400 cursor-not-allowed text-gray-200"
                            : "bg-[#009fe3] text-white hover:bg-[#007cb5]"
                        }`}
                      >
                        {startingClass[batch.id] ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Starting...
                          </span>
                        ) : (
                          "Start Class"
                        )}
                      </button>
                      <button
                        onClick={() => toggleStudentList(batch.id)}
                        className="bg-yellow-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 text-sm"
                      >
                        {expandedBatchId === batch.id
                          ? "Hide Students"
                          : "See Students List"}
                      </button>
                      <button
                        onClick={() => setSelectedBatchId(batch.id)}
                        className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm"
                      >
                        See Attendance Matrix
                      </button>
                    </div>
                  </div>

                  {expandedBatchId === batch.id && batchStudents[batch.id] && (
                    <BatchStudents students={batchStudents[batch.id]} />
                  )}

                  {selectedBatchId === batch.id && (
                    <div className="mt-6">
                      <MentorAttendanceMatrix batchId={batch.id} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MentorBatches;
